import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Switch,
  Grid,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';

const backendUrl = "http://165.232.179.192:1880";
const wsUrl = "ws://165.232.179.192:1880/ws";
const db = getFirestore();

const Home = () => {
  const updateHomeId = localStorage.getItem("updateHomeId");
  const username = localStorage.getItem("username") || "User";
  const [devices, setDevices] = useState([]); // List of devices for the user
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [originalHomeId, setOriginalHomeId] = useState("");
  const [relays, setRelays] = useState([]); // Relays for selected device
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [connectionStatus, setConnectionStatus] = useState("Disconnected");
  const switchRefs = useRef({});
  const userInitiatedMap = useRef({});
  const latestRelayStates = useRef({});
  const webSocketRef = useRef(null);

  // Fetch all devices for the user from Firestore
  const fetchDevices = async () => {
    setLoading(true);
    setError("");
    try {
      const q = query(collection(db, "Users"), where("updateHomeId", "==", updateHomeId));
      const querySnapshot = await getDocs(q);
      let foundDevices = [];
      let foundOriginalHomeId = "";
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        if (userData.devices && userData.devices.length > 0) {
          foundDevices = userData.devices;
          foundOriginalHomeId = userData.devices[0].originalHomeId || "";
        }
      });
      setDevices(foundDevices);
      if (foundDevices.length > 0) {
        setSelectedDeviceId(foundDevices[0].deviceId);
        setOriginalHomeId(foundDevices[0].originalHomeId || foundOriginalHomeId);
        localStorage.setItem("deviceId", foundDevices[0].deviceId);
        localStorage.setItem("originalHomeId", foundDevices[0].originalHomeId || foundOriginalHomeId);
      }
    } catch (err) {
      setError("Error fetching devices");
    } finally {
      setLoading(false);
    }
  };

  // Fetch relays for selected device
  const fetchRelayData = async (deviceId, origHomeId) => {
    if (!deviceId || !origHomeId) return;
    setLoading(true);
    setError("");
    try {
      const relaysRes = await fetch(`${backendUrl}/relays/${deviceId}?originalHomeId=${origHomeId}`);
      const relaysArray = await relaysRes.json();
      const statesRes = await fetch(`${backendUrl}/states/${deviceId}`);
      const statesMap = await statesRes.json();
      const relayList = relaysArray.map(({ relay }) => ({
        relay,
        status: statesMap[relay] === "ON",
      }));
      setRelays(relayList);
    } catch (err) {
      setError("Error fetching relays or states");
    } finally {
      setLoading(false);
    }
  };

  const sendCommand = async (relay, command) => {
    try {
      await fetch(`${backendUrl}/control`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          deviceId: selectedDeviceId,
          updateHomeId,
          relay,
          command,
        }),
      });
    } catch (err) {
      setError("Error sending command");
    }
  };

  const handleSwitchChange = (relay, checked) => {
    // Optimistically update UI
    setRelays((prev) =>
      prev.map((r) =>
        r.relay === relay ? { ...r, status: checked } : r
      )
    );
    if (userInitiatedMap.current[relay]) {
      sendCommand(relay, checked ? "ON" : "OFF");
      userInitiatedMap.current[relay] = false;
    }
  };

  const updateSwitchState = (relayName, value) => {
    latestRelayStates.current[relayName] = value;
    setRelays((prev) =>
      prev.map((r) =>
        r.relay === relayName ? { ...r, status: value === "ON" } : r
      )
    );
    if (switchRefs.current[relayName] && !userInitiatedMap.current[relayName]) {
      switchRefs.current[relayName].checked = value === "ON";
    }
  };

  const connectWebSocket = () => {
    try {
      const socket = new window.WebSocket(wsUrl);
      webSocketRef.current = socket;
      socket.onopen = () => setConnectionStatus("Connected");
      socket.onclose = () => {
        setConnectionStatus("Disconnected");
        setTimeout(() => {
          if (webSocketRef.current) {
            connectWebSocket();
          }
        }, 3000);
      };
      socket.onerror = () => setConnectionStatus("Error");
      socket.onmessage = (msg) => {
        try {
          const json = JSON.parse(msg.data);
          if (json.type !== "state") return;
          if (json.deviceId !== selectedDeviceId) return;
          const { relay, value } = json;
          updateSwitchState(relay, value);
        } catch (err) {
          setError("WebSocket message error");
        }
      };
    } catch (err) {
      setConnectionStatus("Error");
    }
  };

  useEffect(() => {
    fetchDevices();
    connectWebSocket();
    return () => {
      if (webSocketRef.current) {
        webSocketRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch relays when device or originalHomeId changes
  useEffect(() => {
    if (selectedDeviceId && originalHomeId) {
      fetchRelayData(selectedDeviceId, originalHomeId);
    }
  }, [selectedDeviceId, originalHomeId]);

  // Update originalHomeId when device changes
  useEffect(() => {
    if (devices.length > 0 && selectedDeviceId) {
      const found = devices.find((d) => d.deviceId === selectedDeviceId);
      if (found) {
        setOriginalHomeId(found.originalHomeId || "");
        localStorage.setItem("deviceId", found.deviceId);
        localStorage.setItem("originalHomeId", found.originalHomeId || "");
      }
    }
  }, [selectedDeviceId, devices]);

  const currentDate = new Date().toLocaleString("en-IN", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  return (
    <Box p={3} bgcolor="#121212" minHeight="100vh" color="white">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        bgcolor="#1E1E1E"
        p={2}
        borderRadius={3}
        boxShadow="0 2px 12px rgba(0,0,0,0.3)"
      >
        <Box>
          <Typography variant="h5" fontWeight="bold">Hello, {username}!</Typography>
          <Typography variant="body2" color="gray">
            {currentDate}
          </Typography>
        </Box>
        <Button onClick={fetchDevices} variant="contained" color="primary" disabled={loading}>
          Refresh
        </Button>
      </Box>
      <Box mt={2} mb={2}>
        <Typography variant="body2" color={connectionStatus === "Connected" ? "#43e97b" : connectionStatus === "Disconnected" ? "#ff6b6b" : "#ffd600"}>
          WebSocket: {connectionStatus}
        </Typography>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <>
          {/* Device Spinner */}
          <FormControl fullWidth sx={{ mb: 3, maxWidth: 320 }}>
            <InputLabel id="device-select-label">Device</InputLabel>
            <Select
              labelId="device-select-label"
              value={selectedDeviceId}
              label="Device"
              onChange={(e) => setSelectedDeviceId(e.target.value)}
            >
              {devices.map((device) => (
                <MenuItem key={device.deviceId} value={device.deviceId}>{device.deviceName || device.deviceId}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Relays for selected device */}
          <Grid container spacing={3} mt={2}>
            {relays.map(({ relay, status }) => (
              <Grid item xs={12} md={6} key={relay}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    background: "linear-gradient(to right, #1E1E1E, #2A2A2A)",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.4)",
                    transition: "transform 0.3s",
                    '&:hover': {
                      transform: "scale(1.02)"
                    }
                  }}
                  elevation={5}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    my={1.5}
                    px={2}
                    py={1}
                    borderRadius={2}
                    sx={{
                      backgroundColor: status ? "#1e88e5" : "#424242",
                      color: "white",
                      boxShadow: status
                        ? "0 0 10px #1e88e5"
                        : "inset 0 0 4px rgba(255,255,255,0.1)"
                    }}
                  >
                    <Typography variant="body1" fontWeight={500}>
                      {relay}
                    </Typography>
                    <Switch
                      color="default"
                      checked={status}
                      inputRef={(el) => (switchRefs.current[relay] = el)}
                      onMouseDown={() => (userInitiatedMap.current[relay] = true)}
                      onChange={(e) =>
                        handleSwitchChange(relay, e.target.checked)
                      }
                    />
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default Home;
